import type { FlagEvaluation } from '@spotify-confidence/sdk';
import React from 'react';

// This file replaces the small React wrapper we used from
// @spotify-confidence/react. We keep the adapter locally so only
// @spotify-confidence/sdk needs to be lazy-loaded from the AB test provider,
// which keeps the React wrapper package out of the initial bundle while
// preserving the behavior our AB test hooks depend on.

export type ConfidenceClient = {
  config: Record<string, unknown>;
  track: (...args: unknown[]) => void;
  getContext: () => Record<string, unknown>;
  setContext: (context: Record<string, unknown>) => unknown;
  clearContext: () => void;
  contextChanges: (onStateChange?: () => void) => () => void;
  subscribe: (onStateChange?: (...args: unknown[]) => void) => () => void;
  withContext: (context: Record<string, unknown>) => ConfidenceClient;
  evaluateFlag: <T>(path: string, defaultValue: T) => FlagEvaluation<T>;
  getFlag: <T>(path: string, defaultValue: T) => Promise<T>;
};

export const createFallbackConfidenceClient = (): ConfidenceClient => {
  let context: Record<string, unknown> = {};

  return {
    config: {},
    track: () => undefined,
    getContext: () => context,
    setContext: nextContext => {
      context = { ...context, ...nextContext };
      return true;
    },
    clearContext: () => {
      context = {};
    },
    contextChanges: () => () => undefined,
    subscribe: () => () => undefined,
    withContext: nextContext => {
      const child = createFallbackConfidenceClient();
      child.setContext({ ...context, ...nextContext });
      return child;
    },
    evaluateFlag: (_, defaultValue) => ({
      reason: 'UNSPECIFIED',
      value: defaultValue
    }),
    getFlag: async (_, defaultValue) => defaultValue
  };
};

export const fallbackConfidenceClient = createFallbackConfidenceClient();

export const ConfidenceClientContext = React.createContext<ConfidenceClient>(
  fallbackConfidenceClient
);

const useConfidenceClient = () => React.useContext(ConfidenceClientContext);

// Mirrors the useEvaluateFlag behavior we rely on: render-time evaluation,
// rerender on client updates, and Suspense support for thenable results.
export function useConfidenceEvaluateFlag<T>(
  path: string,
  defaultValue: T,
  options?: { enabled?: boolean }
) {
  const confidenceClient = useConfidenceClient();
  const enabled = options?.enabled ?? true;
  const evaluation = enabled
    ? confidenceClient.evaluateFlag(path, defaultValue)
    : {
        reason: 'UNSPECIFIED',
        value: defaultValue
      };
  const [, setRenderTick] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) {
      return () => undefined;
    }

    return confidenceClient.subscribe(() => {
      setRenderTick(current => current + 1);
    });
  }, [confidenceClient, enabled]);

  if ('then' in evaluation) {
    throw evaluation;
  }

  return evaluation;
}
